/**
 * Live Bonsai Bazaar NFT Market collection discovery.
 * Source of truth: on-chain registry jyi55-xqaaa-aaaau-agwlq-cai.
 */
import { createIcpAgent } from "./icAgent.js";
import { normalizeIcpCanisterId } from "./icAgent.js";
/** Bonsai Bazaar NFT Market collection registry (mainnet). */
export const BAZAAR_REGISTRY_CANISTER = "jyi55-xqaaa-aaaau-agwlq-cai";
/** Shared native ICRC-7 mint ledger for Bazaar-native collections. */
export const BAZAAR_SHARED_LEDGER = "ivgzt-yyaaa-aaaau-agwma-cai";
/** Known external Bazaar-imported ledgers (fallback if registry query fails). */
export const BAZAAR_KNOWN_EXTERNAL_LEDGERS = [
    "ghxmp-xiaaa-aaaao-ba4sq-cai", // IC SPICY / PepperHeads
];
function variantKey(v) {
    return Object.keys(v)[0] ?? "Unknown";
}
function mapStatus(status) {
    const k = variantKey(status);
    if (k === "Draft" || k === "Live" || k === "Paused")
        return k;
    return "Unknown";
}
function mapStandard(standard) {
    const k = variantKey(standard);
    if (k === "ICRC7" || k === "EXT" || k === "DIP721" || k === "Custom")
        return k;
    return "Unknown";
}
async function createRegistryActor(canisterId = BAZAAR_REGISTRY_CANISTER) {
    const { Actor } = await import("@dfinity/agent");
    const { IDL } = await import("@dfinity/candid");
    const agent = await createIcpAgent();
    const CollectionStatus = IDL.Variant({
        Draft: IDL.Null,
        Live: IDL.Null,
        Paused: IDL.Null,
    });
    const RwaStatus = IDL.Variant({
        None: IDL.Null,
        Pending: IDL.Null,
        Certified: IDL.Null,
        Rejected: IDL.Null,
        Revoked: IDL.Null,
    });
    const Standard = IDL.Variant({
        ICRC7: IDL.Null,
        EXT: IDL.Null,
        DIP721: IDL.Null,
        Custom: IDL.Null,
    });
    // Minimal CollectionRecord — only fields needed for catalog discovery.
    const CollectionRecordIdl = IDL.Record({
        id: IDL.Nat,
        ledger: IDL.Principal,
        standard: Standard,
        name: IDL.Text,
        symbol: IDL.Text,
        creator: IDL.Principal,
        royaltyBps: IDL.Nat,
        maxSupply: IDL.Nat,
        minted: IDL.Nat,
        featured: IDL.Bool,
        verified: IDL.Bool,
        status: CollectionStatus,
        bannerUrl: IDL.Text,
        description: IDL.Text,
        floorPriceE8s: IDL.Nat,
        listedCount: IDL.Nat,
        volumeE8s: IDL.Nat,
        tags: IDL.Vec(IDL.Text),
        rwaStatus: RwaStatus,
        rwaCertified: IDL.Bool,
        rwaEvidenceUri: IDL.Opt(IDL.Text),
        rwaEvidenceHash: IDL.Opt(IDL.Text),
        rwaReviewerNote: IDL.Opt(IDL.Text),
        rwaCertifiedAt: IDL.Opt(IDL.Nat64),
        rwaCertifiedBy: IDL.Opt(IDL.Principal),
        registryEntryId: IDL.Opt(IDL.Nat),
        shopifyProductHandle: IDL.Opt(IDL.Text),
    });
    const idlFactory = () => IDL.Service({
        listCollections: IDL.Func([IDL.Nat, IDL.Nat], [IDL.Vec(CollectionRecordIdl)], ["query"]),
    });
    return Actor.createActor(idlFactory, { agent, canisterId });
}
function mapRecord(row) {
    const ledger = normalizeIcpCanisterId(row.ledger.toText());
    if (!ledger)
        return null;
    return {
        id: row.id.toString(),
        ledger,
        name: row.name,
        symbol: row.symbol,
        standard: mapStandard(row.standard),
        status: mapStatus(row.status),
        featured: row.featured,
        verified: row.verified,
    };
}
/**
 * Paginate Bazaar registry.listCollections — authoritative live catalog.
 * Continues until a short page; soft safety ceiling is very high (not a product cap).
 */
export async function fetchBazaarCollections(options) {
    const pageSize = options?.pageSize ?? 100;
    const maxPages = options?.maxPages ?? 1_000;
    const actor = await createRegistryActor(options?.registryCanisterId);
    const out = [];
    for (let page = 0; page < maxPages; page++) {
        const offset = BigInt(page * pageSize);
        const rows = await actor.listCollections(offset, BigInt(pageSize));
        if (!rows.length)
            break;
        for (const row of rows) {
            const mapped = mapRecord(row);
            if (mapped)
                out.push(mapped);
        }
        if (rows.length < pageSize)
            break;
    }
    // Registry returns id-desc; product sort for Canopy / vault is ascending id.
    out.sort((a, b) => Number(a.id) - Number(b.id));
    return out;
}
/**
 * Distinct mint-ledger canister IDs currently registered on Bazaar.
 * Always includes shared ledger + known external seeds as a floor.
 */
export async function fetchBazaarLedgerCanisters(options) {
    const set = new Set([
        BAZAAR_SHARED_LEDGER,
        ...BAZAAR_KNOWN_EXTERNAL_LEDGERS,
    ]);
    try {
        const collections = await fetchBazaarCollections(options);
        for (const c of collections)
            set.add(c.ledger);
    }
    catch {
        /* keep seeds — registry may be unreachable from some environments */
    }
    return [...set].sort();
}
